import { useEffect, useRef, useState } from 'react';
import { formatDateShort, formatRs } from '../lib/money';

/**
 * Cost by day — labour, diesel and misc stacked, one bar per working day.
 *
 * Hand-rolled SVG rather than a charting library. CLAUDE.md rule 14 says bundle
 * the chart library and never a CDN script tag, because the app has to work with
 * no signal; for one stacked bar of three series, ~200KB of Chart.js buys
 * nothing and costs a download the tablet may not be able to make.
 *
 * Built to the visualisation rules:
 *   * three categorical series in FIXED order — labour, diesel, misc — never
 *     cycled, and colour follows the series, not its rank, so filtering a day
 *     out cannot repaint the others;
 *   * palettes validated for both surfaces. The dark steps are chosen for the
 *     dark surface rather than flipped from the light ones, which is why they
 *     are darker and not lighter;
 *   * one y axis. Never two;
 *   * 2px surface gap between stacked segments and between bars, 4px rounded
 *     data-end on the top of each stack;
 *   * recessive grid, text in text tokens and never in a series colour;
 *   * a legend always, plus a hover tooltip — and the day-by-day table beneath
 *     the chart is the table view, so identity is never colour alone.
 */

export interface CostPoint {
  date: string;
  labourRs: number;
  dieselRs: number;
  miscRs: number;
}

const SERIES = [
  { key: 'labourRs', label: 'Labour', token: 'var(--series-labour)' },
  { key: 'dieselRs', label: 'Diesel', token: 'var(--series-diesel)' },
  { key: 'miscRs', label: 'Misc', token: 'var(--series-misc)' },
] as const;

const PAD = { top: 10, right: 10, bottom: 30, left: 52 };
const SEGMENT_GAP = 2;
const BAR_RADIUS = 4;
const MAX_BAR = 38;

/** Renders at true pixel size so nothing drops below the 14px floor on a phone. */
function useWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    setWidth(element.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/** A round number above the tallest stack, so the top gridline reads cleanly. */
function niceMax(value: number): number {
  if (value <= 0) return 1000;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    if (step * magnitude >= value) return step * magnitude;
  }
  return 10 * magnitude;
}

function shortRs(value: number): string {
  if (value >= 1_000_000) return `${Math.round(value / 100_000) / 10}m`;
  if (value >= 1000) return `${Math.round(value / 100) / 10}k`;
  return String(value);
}

export default function CostChart({ points, height = 260 }: { points: CostPoint[]; height?: number }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState<number | null>(null);

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;

  const max = niceMax(
    Math.max(0, ...points.map((p) => p.labourRs + p.dieselRs + p.miscRs)),
  );
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  const slot = points.length > 0 ? plotW / points.length : 0;
  const barW = Math.max(2, Math.min(MAX_BAR, slot - SEGMENT_GAP));
  const y = (value: number) => PAD.top + plotH - (value / max) * plotH;

  // Thin the x labels rather than letting them collide.
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(plotW / 56))));

  const hovered = hover !== null ? points[hover] : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`Cost by day, ${points.length} days, labour diesel and misc stacked`}
        >
          {/* Recessive grid: it should be readable, not visible. */}
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke="var(--line)"
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y(tick) + 4}
                textAnchor="end"
                fontSize={11}
                fill="var(--text-3)"
              >
                {shortRs(tick)}
              </text>
            </g>
          ))}

          {points.map((point, index) => {
            const x = PAD.left + index * slot + (slot - barW) / 2;
            const values = SERIES.map((s) => point[s.key]);
            const topIndex = values.reduce((last, v, i) => (v > 0 ? i : last), -1);

            let cursor = PAD.top + plotH;
            return (
              <g key={point.date}>
                {SERIES.map((series, seriesIndex) => {
                  const value = point[series.key];
                  if (value <= 0) return null;
                  const raw = (value / max) * plotH;
                  // The gap is carved out of the segment, so the stack still
                  // sums to the true height.
                  const h = Math.max(1, raw - SEGMENT_GAP);
                  const top = cursor - raw;
                  cursor -= raw;
                  const isTop = seriesIndex === topIndex;

                  return isTop ? (
                    // 4px rounded data-end on the top of the stack only.
                    <path
                      key={series.key}
                      d={roundedTop(x, top, barW, h, Math.min(BAR_RADIUS, h))}
                      fill={series.token}
                      opacity={hover === null || hover === index ? 1 : 0.45}
                    />
                  ) : (
                    <rect
                      key={series.key}
                      x={x}
                      y={top}
                      width={barW}
                      height={h}
                      fill={series.token}
                      opacity={hover === null || hover === index ? 1 : 0.45}
                    />
                  );
                })}

                {index % labelEvery === 0 && (
                  <text
                    x={x + barW / 2}
                    y={height - 10}
                    textAnchor="middle"
                    fontSize={11}
                    fill="var(--text-3)"
                  >
                    {formatDateShort(point.date)}
                  </text>
                )}

                {/* Hit target covers the whole slot, not just the bar. */}
                <rect
                  x={PAD.left + index * slot}
                  y={PAD.top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  onPointerEnter={() => setHover(index)}
                  onPointerLeave={() => setHover(null)}
                />
              </g>
            );
          })}

          <line
            x1={PAD.left}
            x2={width - PAD.right}
            y1={PAD.top + plotH}
            y2={PAD.top + plotH}
            stroke="var(--line-2)"
            strokeWidth={1}
          />
        </svg>
      )}

      {hovered && (
        <div
          className="charttip"
          style={{
            left: Math.min(
              Math.max(PAD.left, PAD.left + (hover! + 0.5) * slot - 80),
              Math.max(0, width - 170),
            ),
          }}
        >
          <div className="name">{formatDateShort(hovered.date)}</div>
          {SERIES.map((series) => (
            <div className="tiprow" key={series.key}>
              <span className="swatch" style={{ background: series.token }} />
              <span className="k">{series.label}</span>
              <span>{formatRs(hovered[series.key])}</span>
            </div>
          ))}
          <div className="tiprow tiptotal">
            <span className="k">Total</span>
            <span>{formatRs(hovered.labourRs + hovered.dieselRs + hovered.miscRs)}</span>
          </div>
        </div>
      )}

      {/* Always present for three series: identity is never colour alone. */}
      <div className="legend">
        {SERIES.map((series) => (
          <span className="legenditem" key={series.key}>
            <span className="swatch" style={{ background: series.token }} />
            {series.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** A bar with its top two corners rounded and its base square on the axis. */
function roundedTop(x: number, y: number, w: number, h: number, r: number): string {
  const radius = Math.min(r, w / 2, h);
  return [
    `M ${x} ${y + h}`,
    `L ${x} ${y + radius}`,
    `Q ${x} ${y} ${x + radius} ${y}`,
    `L ${x + w - radius} ${y}`,
    `Q ${x + w} ${y} ${x + w} ${y + radius}`,
    `L ${x + w} ${y + h}`,
    'Z',
  ].join(' ');
}
